import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useSupplements, useDeleteSupplement } from "@/hooks/use-supplements";
import { useUpdateSupplement } from "@/hooks/useSupplementMutations";
import { FilePlus, ChevronDown, ChevronRight, Plus, Loader2, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from "date-fns";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { SupplementModal } from "./SupplementModal";
import { Comparison, SupplementStatus, Claim } from "@/api/generated/client/models";
import { Supplement } from "@/api/generated/client/models/Supplement";
import { useSupplementGenerationSSE } from "@/hooks/useSSEProgress";
import { useQueryClient } from "@tanstack/react-query";
import { usePaymentMethodGate } from "@/hooks/use-subscription-guard";
import { PaywallModal } from "@/components/subscription/PaywallModal";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";

interface SupplementsModuleProps {
    claimId: number;
    isOpen: boolean;
    onToggle: () => void;
    comparisons: Comparison[];
    claim?: Claim | null;
    canWrite: boolean;
}

export function SupplementsModule({ claimId, isOpen, onToggle, comparisons, claim, canWrite }: SupplementsModuleProps) {
    const {
        supplements = [],
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        totalCount
    } = useSupplements(claimId, 5);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [paywallOpen, setPaywallOpen] = useState(false);
    const [, setLocation] = useLocation();
    const updateSupplement = useUpdateSupplement();
    const { shouldPaywall } = usePaymentMethodGate();

    const handleNewSupplement = () => {
        if (!canWrite) return;
        // Pay-as-you-go: block only when we definitively know there's no payment
        // method on file (see usePaymentMethodGate). The backend still enforces.
        if (shouldPaywall) {
            setPaywallOpen(true);
            return;
        }
        setDialogOpen(true);
    };

    const observerTarget = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            {
                threshold: 0,
                rootMargin: '100px',
                root: scrollContainerRef.current
            }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, isOpen]);

    // totalCount is now provided directly by the hook

    const handleSupplementClick = (supplementId: number) => {
        setLocation(`/supplements/${supplementId}`);
    };

    const handleStatusChange = async (supplementId: number, newStatus: SupplementStatus) => {
        await updateSupplement.mutateAsync({ supplementId, status: newStatus });
    };

    return (
        <>
            <Card className="shadow-xs w-full overflow-hidden">
                <Collapsible open={isOpen} onOpenChange={onToggle}>
                    <CollapsibleTrigger className="w-full">
                        <CardHeader className="py-3 px-4 hover:bg-muted/50 cursor-pointer transition-colors overflow-hidden">
                            <div className="flex items-center justify-between min-w-0 w-full">
                                <div className="flex items-center gap-2 min-w-0">
                                    <FilePlus className="h-4 w-4 shrink-0" />
                                    <CardTitle className="text-sm font-medium truncate">Supplements</CardTitle>
                                    <Badge variant="secondary" className="text-xs h-5 px-1.5 min-w-[1.25rem] shrink-0 flex items-center justify-center">
                                        {totalCount}
                                    </Badge>
                                </div>
                                {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                            </div>
                        </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <CardContent className="px-4 pb-4 pt-0 w-full overflow-hidden">
                            <div className="pb-2 border-b mb-1"></div>

                            {isLoading && supplements.length === 0 ? (
                                <div className="flex justify-center p-8">
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                </div>
                            ) : supplements.length === 0 ? (
                                <div className="text-center py-6 text-sm text-muted-foreground">
                                    No supplements yet
                                </div>
                            ) : (
                                <div
                                    ref={scrollContainerRef}
                                    className="space-y-2 pt-2 max-h-[300px] overflow-y-auto pr-1 overflow-x-hidden scrollbar-thin scrollbar-thumb-muted-foreground/20 hover:scrollbar-thumb-muted-foreground/40"
                                >
                                    {supplements.map((supp) => (
                                        <SupplementListItem
                                            key={supp.id}
                                            supplement={supp}
                                            claimId={claimId}
                                            onStatusChange={handleStatusChange}
                                            onClick={() => handleSupplementClick(supp.id)}
                                            comparisons={comparisons}
                                            canWrite={canWrite}
                                        />
                                    ))}

                                    {/* Load more trigger */}
                                    <div ref={observerTarget} className="h-4 w-full flex justify-center py-2">
                                        {isFetchingNextPage && (
                                            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="pt-3 border-t mt-3 w-full">
                                <Button
                                    size="sm"
                                    className="w-full h-8 text-xs"
                                    onClick={handleNewSupplement}
                                    disabled={comparisons.length === 0 || !canWrite}
                                >
                                    <Plus className="h-3 w-3 mr-2" /> New Supplement
                                </Button>
                            </div>
                        </CardContent>
                    </CollapsibleContent>
                </Collapsible>
            </Card>

            <SupplementModal
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                claimId={claimId}
                comparisons={comparisons}
                claim={claim}
            />
            <PaywallModal
                open={paywallOpen}
                onOpenChange={setPaywallOpen}
                feature="supplement"
            />
        </>
    );
}

/**
 * Individual supplement list item with SSE progress tracking
 */
function SupplementListItem({
    supplement,
    claimId,
    onStatusChange,
    onClick,
    comparisons,
    canWrite,
}: {
    supplement: Supplement;
    claimId: number;
    onStatusChange: (supplementId: number, status: SupplementStatus) => Promise<void>;
    onClick: () => void;
    comparisons: Comparison[];
    canWrite: boolean;
}) {
    const queryClient = useQueryClient();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const deleteSupplement = useDeleteSupplement(claimId);
    
    // Find the comparison for this supplement
    const comparison = comparisons.find(c => c.id === supplement.comparisonId);
    const comparisonName = comparison?.name || `Comparison #${supplement.comparisonId}`;

    // Determine if we should connect to SSE
    const isCompleted = supplement.workflowStatus === 'completed';
    const isFailed = supplement.workflowStatus === 'failed';
    const isActive = !isCompleted && !isFailed;

    // Connect to SSE for active workflows (invalidation handled by useSupplement hook)
    const { progress, isComplete: sseComplete, isFailed: sseFailed } = useSupplementGenerationSSE(
        supplement.id,
        isActive
    );

    // When SSE completes, invalidate queries to refresh data
    useEffect(() => {
        if (sseComplete || sseFailed) {
            // Invalidate supplements list to refresh with latest data from database
            queryClient.invalidateQueries({ queryKey: ['claim', claimId, 'supplements'] });
            // Invalidate notifications to show completion notification in bell
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    }, [sseComplete, sseFailed, claimId, queryClient]);

    // Use SSE state to immediately show completion (before React Query updates)
    const effectivelyCompleted = isCompleted || sseComplete;
    const effectivelyFailed = isFailed || sseFailed;
    const effectivelyActive = !effectivelyCompleted && !effectivelyFailed;

    // Calculate display values
    const showProgress = effectivelyActive && progress?.progress !== undefined && progress.progress >= 0;
    const displayProgress = progress?.progress ?? 0;
    const displayMessage = progress?.message ?? 'Processing...';

    return (
        <>
            <div
                className="p-3 border rounded-md hover:bg-muted/50 transition-colors relative overflow-hidden cursor-pointer w-full min-w-0"
                onClick={() => { if (!deleteOpen) onClick(); }}
            >
                {/* Progress bar for active workflows */}
                {showProgress && (
                    <div
                        className="absolute bottom-0 left-0 h-1 bg-blue-500/20 transition-all duration-500"
                        style={{ width: `${displayProgress}%` }}
                    />
                )}

                <div className="relative z-10 w-full min-w-0">
                    <div className="flex justify-between items-start mb-2 min-w-0 w-full gap-2">
                        <div className="flex-1 min-w-0 overflow-hidden">
                            <div className="flex items-center gap-2 mb-1 min-w-0">
                                <span className="font-medium text-xs truncate flex-1 min-w-0" title={supplement.name || `Supplement #${supplement.id}`}>
                                    {supplement.name || `Supplement #${supplement.id}`}
                                </span>
                                {(supplement as any).state && (
                                    <Badge variant="outline" className="text-[10px] uppercase font-bold px-1.5 h-4 border-muted-foreground/30 text-muted-foreground shrink-0">
                                        {(supplement as any).state}
                                    </Badge>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate w-full" title={comparisonName}>
                                {comparisonName}
                            </p>
                        </div>
                        {supplement.createdAt && (
                            <div className="flex items-center gap-1 shrink-0 mt-0.5">
                                <span className="text-xs text-muted-foreground">
                                    {format(new Date(supplement.createdAt), 'MMM d')}
                                </span>
                                {canWrite && (
                                    <button
                                        className="p-0.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                                        onClick={(e) => { e.stopPropagation(); setDeleteOpen(true); }}
                                        aria-label={`Delete supplement ${supplement.name || `Supplement #${supplement.id}`}`}
                                        title="Delete supplement"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Workflow Status with SSE updates */}
                {effectivelyActive && (
                    <div className="flex items-center gap-1 mt-2 relative z-10">
                        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{displayMessage}</span>
                    </div>
                )}
                {effectivelyFailed && (
                    <div className="flex items-center gap-1 mt-2 relative z-10">
                        <span className="text-xs text-red-600 font-medium">Failed</span>
                    </div>
                )}
                {effectivelyCompleted && (
                    <div className="flex items-center gap-1 mt-2 relative z-10">
                        <span className="text-xs text-green-600 font-medium">Completed</span>
                    </div>
                )}

                {/* Status Dropdown - only show when workflow is completed AND data is persisted */}
                {isCompleted && (
                    <div className="mt-2 flex items-center gap-2 relative z-10" onClick={(e) => e.stopPropagation()}>
                        <span className="text-xs text-muted-foreground">Status:</span>
                        <Select
                            value={supplement.status}
                            onValueChange={(value) => {
                                if (!canWrite) return;
                                onStatusChange(supplement.id, value as SupplementStatus);
                            }}
                            disabled={!canWrite}
                        >
                            <SelectTrigger className="h-7 w-32 text-xs">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="draft" className="text-xs">Draft</SelectItem>
                                <SelectItem value="submitted" className="text-xs">Submitted</SelectItem>
                                <SelectItem value="approved" className="text-xs">Approved</SelectItem>
                                <SelectItem value="rejected" className="text-xs">Rejected</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                onConfirm={async () => { await deleteSupplement.mutateAsync(supplement.id); }}
                title="Delete Supplement"
                description={`Are you sure you want to delete "${supplement.name || `Supplement #${supplement.id}`}"? This will remove all justifications and generated content. This action cannot be undone.`}
            />
        </>
    );
}